import React, { useState } from 'react';
import { Icon } from '@fikasio/react-ui-components';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import ListToolbar, { viewModeGroupClassName } from 'components/UI/ListToolbar';
import { Objective } from 'services/objectives/types';
import { RangeSelectEvent } from 'utils/rangeSelection';
import ObjectivesBoard from './ObjectivesBoard';
import ObjectivesList from './ObjectivesList';

interface ObjectivesViewerProps {
  actions?: React.ReactNode;
  objectives?: Objective[] | null;
  onAddObjective: (objective: Partial<Objective>) => Promise<unknown>;
  onObjectiveClick: (objectiveId: string) => void;
  onObjectiveSelect: (
    objective: Objective,
    event?: RangeSelectEvent,
    displayedObjectives?: Objective[],
  ) => void;
  onSelectAllObjectives: (objectives: Objective[]) => void;
  projectId?: string;
  selectedObjectives: Objective[];
  showViewModeButtons?: boolean;
}

const ObjectivesViewer = ({
  actions,
  objectives,
  onAddObjective,
  onObjectiveClick,
  onObjectiveSelect,
  onSelectAllObjectives,
  projectId,
  selectedObjectives,
  showViewModeButtons,
}: ObjectivesViewerProps) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('LIST');

  let objectiveView;
  if (viewMode === 'LIST') {
    objectiveView = (
      <ObjectivesList
        onAddObjective={onAddObjective}
        onObjectiveClick={onObjectiveClick}
        onSelectAllObjectives={onSelectAllObjectives}
        onObjectiveSelect={onObjectiveSelect}
        projectId={projectId}
        selectedObjectives={selectedObjectives}
        objectives={objectives}
      />
    );
  } else if (viewMode === 'BOARD') {
    objectiveView = (
      <ObjectivesBoard
        objectives={objectives}
        onObjectiveClick={onObjectiveClick}
        onObjectiveSelect={onObjectiveSelect}
        selectedObjectives={selectedObjectives}
      />
    );
  } else {
    objectiveView = null;
  }

  return (
    <>
      {
        (showViewModeButtons || actions) && (
          <ListToolbar
            start={showViewModeButtons && (
              <div className={viewModeGroupClassName}>
                <Button
                  active={viewMode === 'LIST'}
                  aria-label={t('listView')}
                  aria-pressed={viewMode === 'LIST'}
                  onClick={() => setViewMode('LIST')}
                  variant="outline-secondary"
                >
                  <Icon name="list" size="1x" />
                </Button>
                <Button
                  active={viewMode === 'BOARD'}
                  aria-label={t('cardsView')}
                  aria-pressed={viewMode === 'BOARD'}
                  onClick={() => setViewMode('BOARD')}
                  variant="outline-secondary"
                >
                  <Icon name="th" size="1x" />
                </Button>
              </div>
            )}
          >
            {actions}
          </ListToolbar>
        )
      }
      { objectives && objectiveView }
    </>
  );
};

export default ObjectivesViewer;
